import { useCallback } from 'react';

import useGroupsList from '@/components/Form/components/VisibilitySettings/components/RestrictedVisibilitySettingsModal/components/RestrictedVisibilitySettingsGroupsTab/hooks/useGroupsList';
import useVisibilitySettings from '@/components/Form/components/VisibilitySettings/hooks/useVisibilitySettings';

import { RestrictedVisibilitySettingsGroupsTabType } from '../types';

const useRestrictedVisibilitySettingsGroupsTab = (
  props: RestrictedVisibilitySettingsGroupsTabType & {
    debounceSearchText: string;
    searchText: string;
  },
) => {
  const { value, onChange, contextualGroupsConfig, debounceSearchText, searchText } = props;

  const {
    values: { groupIds, contextualGroups },
    methods: { onAddGroup, onAddMultipleGroups, onRemoveGroup },
  } = useVisibilitySettings({ value, onChange, contextualGroupsConfig });

  const {
    groups,
    isGroupsFetching,
    fetchMoreGroups,
    toggleSelectedContextualGroupIds: _toggleSelectedContextualGroupIds,
    selectedGroupIdsSet,
    selectedContextualGroupsSet,
    toggleSelectedGroupIds: _toggleSelectedGroupIds,
  } = useGroupsList({
    supportedContextualGroups: contextualGroupsConfig?.supportedContextualGroups,
    debounceSearchText,
    searchText,
    selectedGroupIds: groupIds,
    selectedContextualGroups: contextualGroups,
  });

  const toggleSelectedContextualGroupIds: ReturnType<
    typeof useGroupsList
  >['toggleSelectedContextualGroupIds'] = useCallback(
    (key) => {
      selectedContextualGroupsSet.has(key)
        ? onRemoveGroup({ removedContextualGroupId: key })
        : onAddGroup({ contextualGroup: key });
      _toggleSelectedContextualGroupIds(key);
    },
    [onAddGroup, onRemoveGroup, selectedContextualGroupsSet, _toggleSelectedContextualGroupIds],
  );

  const toggleSelectedGroupIds: ReturnType<typeof useGroupsList>['toggleSelectedGroupIds'] =
    useCallback(
      (key) => {
        selectedGroupIdsSet.has(key)
          ? onRemoveGroup({ removedGroupId: key })
          : onAddGroup({ groupId: key });
        _toggleSelectedGroupIds(key);
      },
      [onAddGroup, onRemoveGroup, selectedGroupIdsSet, _toggleSelectedGroupIds],
    );

  const bulkImportGroups = useCallback(
    (newGroupIds: string[]) => {
      onAddMultipleGroups({ newGroupIds });
      for (const groupId of newGroupIds) {
        _toggleSelectedGroupIds(groupId);
      }
    },
    [onAddMultipleGroups],
  );

  return {
    groups,
    isGroupsFetching,
    fetchMoreGroups,
    toggleSelectedContextualGroupIds,
    selectedGroupIdsSet,
    selectedContextualGroupsSet,
    toggleSelectedGroupIds,
    bulkImportGroups,
  };
};

export default useRestrictedVisibilitySettingsGroupsTab;
